import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  ActivateAccountPropsInterface,
  LoginPropsInterface,
  RequestPasswordResetPropsInterface,
  ResetPasswordPropsInterface,
  UpdateAccountPropsInterface,
} from "./authTypes";
import { store } from "../../app/store";

const url = process.env.REACT_APP_BACKEND_URL;

export const updateAuthAccountRequest = createAsyncThunk(
  "auth/updateAccount",
  async (data: UpdateAccountPropsInterface, thunkAPI) => {
    const token = store.getState().auth.bearerToken;
    const response = await fetch(`${url}/api/auth/update-account`, {
      method: "PUT",
      body: JSON.stringify({
        password: data.password,
        password_confirmation: data.passwordConfirmation,
        old_password: data.currentPassword,
      }),
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.json();
  }
);

export const loginRequest = createAsyncThunk(
  "auth/login",
  async (data: LoginPropsInterface, thunkAPI) => {
    const response = await fetch(`${url}/api/auth/login`, {
      method: "POST",
      body: JSON.stringify({
        email: data.email,
        password: data.password,
      }),
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    return response.json();
  }
);

export const activateAccountRequest = createAsyncThunk(
  "auth/activateAccount",
  async (data: ActivateAccountPropsInterface & { token: string }, thunkAPI) => {
    const response = await fetch(`${url}/api/activate`, {
      method: "POST",
      body: JSON.stringify({
        password: data.password,
        password_confirmation: data.passwordConfirmation,
        token: data.token,
      }),
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    return response.json();
  }
);

export const logoutRequest = createAsyncThunk(
  "auth/logout",
  async (_, thunkAPI) => {
    const token = store.getState().auth.bearerToken;
    const response = await fetch(`${url}/api/auth/logout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.json();
  }
);

export const resetPasswordRequest = createAsyncThunk(
  "auth/resetPassword",
  async (data: ResetPasswordPropsInterface & { token: string }, thunkAPI) => {
    const response = await fetch(`${url}/api/password-reset/reset`, {
      method: "POST",
      body: JSON.stringify({
        password: data.password,
        password_confirmation: data.passwordConfirmation,
        token: data.token,
      }),
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    if (response.status === 429) {
      // Próbuj odczytać Retry-After z nagłówków (różne warianty nazwy)
      let retryAfter =
        response.headers.get("Retry-After") ||
        response.headers.get("retry-after") ||
        response.headers.get("X-RateLimit-Reset");

      // Jeśli nie ma w nagłówkach, sprawdź response body
      if (!retryAfter) {
        const data = await response.json();
        retryAfter = data?.retryAfter || data?.retry_after || null;

        return thunkAPI.rejectWithValue({
          status: 429,
          retryAfter: retryAfter,
          message: data?.message,
        });
      }

      return thunkAPI.rejectWithValue({
        status: 429,
        retryAfter: retryAfter,
      });
    }

    if (response.status === 400) {
      const errorData = await response.json();
      return thunkAPI.rejectWithValue({
        status: 400,
        message: errorData?.message || "Token jest nieprawidłowy lub wygasł",
      });
    }

    return response.json();
  }
);

export const validatePasswordResetTokenRequest = createAsyncThunk(
  "auth/validatePasswordResetToken",
  async (token: string, thunkAPI) => {
    const response = await fetch(`${url}/api/password-reset/validate/${token}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    if (response.status === 422 || response.status === 400 || !response.ok) {
      const errorData = await response.json();
      return thunkAPI.rejectWithValue({
        status: response.status,
        message: errorData?.message || "Token jest nieprawidłowy lub wygasł",
      });
    }

    return response.json();
  }
);

export const requestPasswordResetRequest = createAsyncThunk(
  "auth/requestPasswordReset",
  async (data: RequestPasswordResetPropsInterface, thunkAPI) => {
    const response = await fetch(`${url}/api/password-reset/request`, {
      method: "POST",
      body: JSON.stringify({
        email: data.email,
      }),
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    if (response.status === 429) {
      // Próbuj odczytać Retry-After z nagłówków (różne warianty nazwy)
      let retryAfter =
        response.headers.get("Retry-After") ||
        response.headers.get("retry-after") ||
        response.headers.get("X-RateLimit-Reset");

      // Jeśli nie ma w nagłówkach, sprawdź response body
      if (!retryAfter) {
        const data = await response.json();
        retryAfter = data?.retryAfter || data?.retry_after || null;

        return thunkAPI.rejectWithValue({
          status: 429,
          retryAfter: retryAfter,
          message: data?.message,
        });
      }

      return thunkAPI.rejectWithValue({
        status: 429,
        retryAfter: retryAfter,
      });
    }

    return response.json();
  }
);
